import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { getPost } from "@/data/posts";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, canonicalLinks, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl } from "@/lib/seo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import pricingSpreadsheetCartonsImg from "@/assets/photos/pricing-spreadsheet-cartons.webp";

const SLUG = "pricing-tools-5-billion-business";
const TITLE = "The pricing tools a $5 billion business runs on";
const DESCRIPTION =
  "What building pricing systems in a $5 billion beverage business teaches a $5M business: the system and the owner, not the size, that make prices hold.";
const CATEGORY = "Pricing";
const READ = "4 min read";

export const Route = createFileRoute("/insights/pricing-tools-5-billion-business")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage(`insights-${SLUG}`, TITLE),
      ogUrl(`/insights/${SLUG}`),
    ],
    links: canonicalLinks(`/insights/${SLUG}`),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: TITLE, path: `/insights/${SLUG}` }])),
      jsonLd(
        webPageSchema({
          path: `/insights/${SLUG}`,
          name: `${TITLE} | Ajility`,
          description: DESCRIPTION,
          dateModified: modifiedAt(SLUG),
          mainEntityId: `${absoluteUrl(`/insights/${SLUG}`)}#article`,
          image: `insights-${SLUG}`,
        }),
      ),
      jsonLd(
        articleSchema({
          title: TITLE,
          description: DESCRIPTION,
          slug: SLUG,
          datePublished: publishedAt(SLUG),
          dateModified: modifiedAt(SLUG),
          section: CATEGORY,
        }),
      ),
    ],
  }),
  component: PricingTools5BillionBusiness,
});

function PricingTools5BillionBusiness() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{CATEGORY}, {READ}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(SLUG)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{DESCRIPTION}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>


      <PhotoBand src={pricingSpreadsheetCartonsImg} alt="A laptop showing a pricing spreadsheet on a bench beside stacked cardboard product cartons in a small distribution warehouse." />

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <article className="article-body">
          <p>Inside a $5 billion beverage business, earlier in my career, I built the pricing guidance tool the national sales team ran on. Worth about $2.5M a year, and it won the company-wide Innov8 award in 2013.</p>
          <p>Then a contract pricing tool worth about $1M a year plus $300K in FTE savings. A diagnostic across $10M of processed customer credits. A pricing platform serving 20,000 customers.</p>
          <p>Here's what those years taught me: big companies don't price better because they're smarter. They price better because they have a system, and someone owns it. Every discount has an approval path. Every rebate is reconciled against what was actually achieved. Every override leaves a trail.</p>
          <p>A $5M business can have the same discipline with a spreadsheet and a monthly rhythm. What it can't survive is nobody owning the number.</p>
          <p>Who owns pricing in your business? If the answer is "everyone", it's no one.</p>
          <p>
            <Link to="/specialisations/pricing-and-revenue-management" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">
              See the pricing and revenue management specialisation
            </Link>
          </p>
          <p>For the full case, read <Link to="/insights/$slug" params={{ slug: "pricing-tools-coca-cola-amatil" }} className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">{getPost("pricing-tools-coca-cola-amatil")?.title}</Link>.</p>
        </article>
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
            Want to know where your real bottleneck is?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
            Book a 30-minute fit call, a structured conversation rather than a pitch, about
            where the constraint actually sits in your business.
          </p>
          <BookFitCall
            placement={`insights_article_${SLUG}_footer`}
            buttonClassName="mt-8 inline-flex items-center gap-2 rounded-full bg-frost px-7 py-3.5 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <p className="mt-8 font-serif italic text-frost/80">
            "Strategy without execution is hallucination."
          </p>
        </div>
      </section>
    </div>
  );
}
